<!-- Chat message composer textarea with attachment previews and send button. -->

<script lang="ts" setup>
import { animateOnce } from '@nzyme/dom-utils/animateOnce.js';
import { isProbablyMobile } from '@nzyme/dom-utils/isProbablyMobile.js';
import { TextArea } from '@nzyme/vue-forms/components/TextArea.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { default as TransitionList } from '@nzyme/vue-transitions/TransitionList.vue';
import { vFocus } from '@nzyme/vue-utils/directives/vFocus.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { Reveal } from '@nzyme/vue/components/Reveal.jsx';
import { useWindowSize } from '@vueuse/core';
import { ArrowUp } from 'lucide-vue-next';
import { computed, nextTick, onMounted, ref, useCssModule } from 'vue';
import type { PropType } from 'vue';

import { useShortcut } from '@sigma/client/utils/useShortcut.js';
import type { FileInput } from '@sigma/files-common/FileInput.js';
import { breakpoints } from '@sigma/stylebook/breakpoints.js';

import Button from '../Button.vue';
import * as l from './ChatInput.loc.js';
import ChatInputAttachment from './ChatInputAttachment.vue';

type Events = {
  send: [];
  deleteFile: [file: FileInput];
};

const props = defineProps({
  focus: Boolean,
  detached: Boolean,
  placeholder: String,
  submitButton: { type: Boolean, default: true },
  files: Array as PropType<FileInput[]>,
  disabled: Boolean,
});

defineEmits<Events>();

const { width } = useWindowSize();
const isMobile = computed(() => width.value < breakpoints.md || isProbablyMobile());
const message = defineModel<string>();
const slots = defineSlots<{ default: [] }>();
const css = useCssModule('css');
const t = useTranslate();
const emitAsync = useEmitAsync<Events>();
const textareaRef = ref<InstanceType<typeof TextArea>>();

const showActions = computed(() => props.submitButton || !!slots.default);

const submitShortcut = useShortcut({
  key: 'Enter',
  cmd: true,
});

onMounted(() => {
  // Autofocus so the user can start typing immediately.
  // Skipped on mobile to avoid popping the virtual keyboard on open.
  if (isMobile.value) {
    return;
  }
  const textareaEl = textareaRef.value?.$el as HTMLTextAreaElement | undefined;
  textareaEl?.focus();
});

async function handleSend() {
  message.value = message.value?.trim();
  await nextTick();

  const hasFiles = props.files && props.files.length > 0;

  if (!message.value && !hasFiles) {
    // Add a shake animation to the textarea to indicate that the message is empty
    const textareaEl = textareaRef.value?.$el as HTMLElement | undefined;
    if (textareaEl) {
      animateOnce(textareaEl, css.textareaShake);
    }

    return;
  }

  await emitAsync('send');

  // Refocus the textarea so the user can keep typing.
  // Skipped on mobile to avoid popping the virtual keyboard back up.
  if (!isMobile.value) {
    const textareaEl = textareaRef.value?.$el as HTMLTextAreaElement | undefined;
    textareaEl?.focus();
  }
}
</script>

<template>
  <div
    tid="chat-input"
    :class="[css.chatInput, detached && css.detached]"
  >
    <div :class="css.inputContainer">
      <Reveal>
        <div
          v-if="files && files.length > 0"
          :class="css.attachmentsWrapper"
        >
          <TransitionList
            :class="css.attachments"
            horizontal
          >
            <ChatInputAttachment
              v-for="file in files"
              :key="file.tempId"
              :file-name="file.name"
              :file-type="file.type"
              :preview-url="file.url"
              :width="file.width"
              :height="file.height"
              :progress="file.progress"
              :error="file.error"
              @delete="emitAsync('deleteFile', file)"
            />
          </TransitionList>
        </div>
      </Reveal>

      <div :class="css.textareaContainer">
        <TextArea
          ref="textareaRef"
          v-model="message"
          v-focus="focus"
          :class="[css.textarea, showActions && css.textarea_withActions]"
          :placeholder="placeholder || t(l.placeholder)"
        />

        <div
          v-if="showActions"
          :class="css.actions"
        >
          <slot />

          <template v-if="submitButton">
            <Button
              v-if="isMobile"
              tid="send-button"
              :class="[css.buttonMobile, css.pushRight]"
              rounded
              variant="primary"
              color="black"
              :title="t(l.send)"
              :disabled="disabled"
              @click="handleSend"
            >
              <ArrowUp :size="20" />
            </Button>

            <Button
              v-else
              tid="send-button"
              variant="primary"
              color="black"
              rounded
              :disabled="disabled"
              :class="css.pushRight"
              :shortcut="submitShortcut"
              :label="t(l.send)"
              @click="handleSend"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module="css">
.chatInput {
  display: flex;
  flex-direction: column;

  @include media-min-lg {
    background: linear-gradient(to bottom, transparent, $color-app-background, $color-app-background);
  }

  &.detached {
    @include media-min-lg {
      padding: 0;
      background: none;
    }
  }
}

.inputContainer {
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  position: relative;
  box-shadow: 0px -4px 32px -8px rgba(0, 0, 0, 0.1);
  background: $color-white;
  border-radius: $border-radius-24 $border-radius-24 0 0;

  @include media-min-md {
    border-radius: $border-radius-24;
    border: 1px solid $color-gray-100;
  }

  .detached & {
    border-radius: $border-radius-12;
    box-shadow: none;
    border-color: $color-gray-300;
  }
}

.attachmentsWrapper {
  height: 180px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: $spacing-16 0 $spacing-04 0;
  flex-shrink: 0;

  @include scrollbar-light($width: 6px, $color: $color-gray-100, $padding: $spacing-04, $margin: $spacing-16);
}

.attachments {
  display: flex;
  gap: $spacing-12;
  height: 100%;

  // There is a CSS quirk that causes right padding to not show correctly
  // We have to use pseudo-elements to fix it
  &::before,
  &::after {
    content: '';
    // Minus the gap between the attachments
    flex: 0 0 #{$spacing-16 - $spacing-12};
  }
}

.textareaContainer {
  position: relative;
  flex: 1;
  display: flex;
}

.textarea {
  resize: none;
  border: none;
  outline: none;
  font-size: $font-size-14;
  width: 100%;
  padding: $spacing-20 $spacing-16;
  line-height: 1.5;
  background: transparent;
  overflow: hidden;

  &::placeholder {
    color: $color-text-secondary;
  }

  &_withActions {
    padding-bottom: $spacing-64;
  }
}

.textareaShake {
  animation: shake 0.3s ease-in-out;
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  50% {
    transform: translateX(5px);
  }
  75% {
    transform: translateX(-2.5px);
  }
  100% {
    transform: translateX(2.5px);
  }
}

.actions {
  position: absolute;
  bottom: $spacing-12;
  left: 0;
  right: 0;
  display: flex;
  gap: $spacing-08;
  align-items: center;
  padding: 0 $spacing-12;
  pointer-events: none;

  > * {
    pointer-events: all;
  }
}

.buttonMobile {
  --button-padding: 0;
  width: 40px;
  height: 40px;
}

.pushRight {
  margin-left: auto;
}
</style>
